/**
 * AddOnTool Store
 *
 * Manages the add-on tools available to the current user and related lookups.
 */

import _ from 'lodash';
import { createSelector } from 'reselect';
import { SeeqNames } from '@seeqdev/constants';
import { type GetStoreState, type SetStoreState, createStore } from '@seeqdev/utils';
import type { AddOnToolOutputV1 } from '@seeqdev/api/model/AddOnToolOutputV1';

export interface AddOnToolState {
  addOnTools: AddOnToolOutputV1[];
}

const initialState: AddOnToolState = {
  addOnTools: [],
};

const addOnToolActionsFactory = (set: SetStoreState<AddOnToolState>, _get: GetStoreState<AddOnToolState>) => ({
  /**
   * Set all the add-on tools available to the current user, regardless of launch location. Consumers select the
   * subset they care about with useHomeScreenAddOnTools() or useToolPaneAddOnTools().
   */
  setAddOnTools: (addOnTools: AddOnToolOutputV1[] = []) => {
    set((draft: AddOnToolState) => {
      draft.addOnTools = addOnTools;
    });
  },
});

const [AddOnToolStoreProvider, useAddOnToolStoreInstance, useAddOnToolSelector] = createStore(
  'AddOnTool',
  initialState,
  addOnToolActionsFactory,
);

export { AddOnToolStoreProvider };

const selectAddOnTools = (state: AddOnToolState) => state.addOnTools;

const homeScreenAddOnToolsSelector = createSelector(selectAddOnTools, (addOnTools) =>
  _.filter(addOnTools, (addOnTool) => addOnTool.launchLocation === SeeqNames.API.AddOnToolLaunchLocation.Homescreen),
);

const toolPaneAddOnToolsSelector = createSelector(selectAddOnTools, (addOnTools) =>
  _.filter(
    addOnTools,
    (addOnTool) =>
      !addOnTool.launchLocation || addOnTool.launchLocation === SeeqNames.API.AddOnToolLaunchLocation.ToolsPane,
  ),
);

/**
 * Get add-on tool store's actions
 */
export const useAddOnToolActions = () => useAddOnToolStoreInstance().actions;

/**
 * Get Home Screen add-on tools
 */
export const useHomeScreenAddOnTools = () => useAddOnToolSelector(homeScreenAddOnToolsSelector);

/**
 * Get the add-on tools that launch from the worksheet tools pane. Tools without a launch location predate the
 * setting and default to the tools pane.
 */
export const useToolPaneAddOnTools = () => useAddOnToolSelector(toolPaneAddOnToolsSelector);
